import {
  ArrowForward,
  HelpOutline,
  Power,
  PowerOff,
  SignalWifiOff,
  WarningOutlined,
  Wifi,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import pages from 'src/router/routes';
import { formatDateTime } from 'src/utils/dateHelper';
import { getUtilityUnits, roundUsage } from 'src/utils/formatters';
import { Alert } from 'switch-shared/modules/crud/alert/alert.entity';
import { MeterPoint } from 'switch-shared/modules/crud/meter-point/meter-point.entity';
import { SearchMeter } from 'switch-shared/modules/crud/meter/meter.dto';
import {
  UtilityType,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';

import './CustomPopup.css';

interface Props {
  meter: SearchMeter;
  alerts?: Alert[];
  meterPoints?: MeterPoint[];
}

const MeterPopUp = ({ meter, alerts, meterPoints }: Props) => {
  const LIMIT = 2;

  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationScopedChildPath } = useAdminUrlNavigation();

  /* States */

  /* API Calls */

  /* Functions & Functional Components */

  // Check if any associated meter point is a bulk meter
  const isBulkMeter = meterPoints?.some(
    (mp) => mp.bulk && meter.meterPointIds?.includes(mp._id)
  );

  // Determine if we should show On/Off status (only for prepaid electricity meters)
  const showOnOffStatus =
    meter.isPrepaid && meter.utilityType === UtilityType.ELECTRICITY;

  return (
    <Popup>
      <Card
        sx={{
          minWidth: 360,
          maxWidth: 450,
          backgroundColor: 'rgba(0,0,0,0)',
          borderRadius: '0',
        }}
      >
        {/* Header with meter name and action button */}
        <CardHeader
          title={meter.name}
          titleTypographyProps={{
            variant: 'h3',
            fontWeight: 'bold',
            lineHeight: '1.2',
            sx: { color: theme.colors.primary.main },
          }}
          action={
            <IconButton
              color='primary'
              title='Open meter'
              style={{ position: 'absolute', right: 0 }}
              onClick={() =>
                navigate(
                  getApplicationScopedChildPath(
                    'meters',
                    meter._id,
                    pages.meters.view.path.replace(':id', meter._id)
                  )
                )
              }
            >
              <ArrowForward />
            </IconButton>
          }
          sx={{
            padding: 0,
            position: 'relative',
            paddingRight: 8,
          }}
        />

        {/* Status Tags Row - Right under the name */}
        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 2, mt: 1 }}
          flexWrap='wrap'
          useFlexGap
        >
          {/* Online/Offline Status */}
          <Tooltip
            title={
              meter.lastSeen !== undefined
                ? `Last seen: ${formatDateTime(meter.lastSeen)}`
                : 'No last seen'
            }
          >
            <Chip
              icon={
                meter.isOnline ? (
                  <Wifi sx={{ color: 'inherit !important' }} />
                ) : (
                  <SignalWifiOff sx={{ color: 'inherit !important' }} />
                )
              }
              label={meter.isOnline ? 'Online' : 'Offline'}
              size='small'
              sx={{
                backgroundColor: meter.isOnline
                  ? theme.tokens.colors.success
                  : theme.tokens.colors.destructive,
              }}
            />
          </Tooltip>

          {/* Bulk/Non-Bulk Status */}
          {isBulkMeter && (
            <Chip
              label={isBulkMeter ? 'Bulk Meter' : 'Sub-meter'}
              size='small'
              sx={{
                backgroundColor: isBulkMeter
                  ? theme.colors.primary.main
                  : theme.colors.neutral.grey,
              }}
            />
          )}

          {/* On/Off Status (only for prepaid electricity meters) */}
          {showOnOffStatus && (
            <Tooltip
              title={
                meter.isOn === undefined
                  ? 'No relay state available for this meter'
                  : meter.isOn
                    ? ''
                    : 'Power supply to this meter is currently off'
              }
            >
              <Chip
                icon={
                  meter.isOn === undefined ? (
                    <HelpOutline sx={{ color: 'inherit !important' }} />
                  ) : meter.isOn ? (
                    <Power sx={{ color: 'inherit !important' }} />
                  ) : (
                    <PowerOff sx={{ color: 'inherit !important' }} />
                  )
                }
                label={
                  meter.isOn === undefined
                    ? 'Unknown'
                    : meter.isOn
                      ? 'On'
                      : 'Off'
                }
                size='small'
                sx={{
                  backgroundColor:
                    meter.isOn === undefined
                      ? theme.colors.neutral.grey[60]
                      : meter.isOn
                        ? theme.tokens.colors.success
                        : theme.tokens.colors.destructive,
                  color: 'white',
                }}
              />
            </Tooltip>
          )}
        </Stack>

        <CardContent sx={{ padding: 0, paddingBottom: '0 !important' }}>
          {/* Two-column layout for data fields */}
          <Grid container spacing={2}>
            {/* Left Column */}
            <Grid item xs={6}>
              {/* EID */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant='h5'
                  sx={{ color: theme.colors.primary.main }}
                >
                  EID
                </Typography>
                <Typography variant='subtitle1'>
                  {meter.eid ?? 'N/A'}
                </Typography>
              </Box>

              {/* Last Reading */}
              <Box>
                <Typography
                  variant='h5'
                  sx={{ color: theme.colors.primary.main }}
                >
                  Last Reading
                </Typography>
                <Typography variant='subtitle1'>
                  {meter.P1 !== undefined
                    ? `${roundUsage(
                        convertToAndFromBase(
                          (meter.P1 ?? 0) + (meter.offsetP1 ?? 0),
                          meter.utilityType,
                          true
                        ),
                        2
                      )} ${getUtilityUnits(meter.utilityType, true)}`
                    : 'No reading'}
                </Typography>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={6}>
              {/* Last Seen */}
              <Box>
                <Typography
                  variant='h5'
                  sx={{ color: theme.colors.primary.main }}
                >
                  Last Seen
                </Typography>
                <Typography variant='subtitle1'>
                  {meter.lastSeen
                    ? formatDateTime(meter.lastSeen)
                    : 'No last seen'}
                </Typography>
              </Box>
            </Grid>

            {/* Full Width - Active Alerts */}
            <Grid item xs={12}>
              <List
                dense={true}
                component='nav'
                sx={{ padding: 0 }}
                subheader={
                  <ListSubheader
                    id='nested-list-subheader'
                    sx={{
                      padding: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      color: theme.colors.primary.main,
                      fontSize: theme.typography.h5,
                      lineHeight: 'normal',
                      marginBottom: 0.5,
                    }}
                  >
                    Active Alerts{' '}
                    {(alerts ?? []).length !== 0 && (
                      <WarningOutlined color='warning' sx={{ mb: -0.5 }} />
                    )}
                  </ListSubheader>
                }
              >
                {(alerts ?? []).slice(0, LIMIT).map((ele) => (
                  <ListItem
                    key={ele._id}
                    sx={{
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    <ListItemText
                      primary={`\u2022 ${ele.cause.description}`}
                      primaryTypographyProps={{
                        variant: 'h5',
                      }}
                    />
                  </ListItem>
                ))}
                {(alerts ?? []).length > LIMIT && (
                  <ListItem sx={{ padding: 0 }}>
                    <ListItemText
                      primary={`and ${(alerts?.length ?? 0) - LIMIT} more...`}
                      primaryTypographyProps={{
                        variant: 'h5',
                      }}
                    />
                  </ListItem>
                )}
                {(alerts ?? []).length === 0 && (
                  <ListItemText
                    primary={`None`}
                    primaryTypographyProps={{
                      variant: 'subtitle1',
                    }}
                  />
                )}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Popup>
  );
};

export default MeterPopUp;
